// src/components/CaseStatusTimelineChart.js
import React, { useMemo, useRef } from 'react'; // Added useRef
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { Box, Typography, CircularProgress, Alert, IconButton, useTheme, useMediaQuery } from '@mui/material'; // Added IconButton, useTheme, useMediaQuery
import DownloadIcon from '@mui/icons-material/Download'; // Changed from ShareIcon
import { getEchartsWatermark, handleEchartsDownload } from '../utils/echartsUtils'; // Import ECharts utilities

// Register necessary ECharts components
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  BarChart,
  CanvasRenderer
]);

// Helper function to format date as YYYY-MM-DD
const formatDate = (date) => {
  if (!date) return null;
  try {
    return new Date(date).toISOString().split('T')[0];
  } catch (e) {
    console.error("Error formatting date:", date, e);
    return null;
  }
};

// Helper function to determine the status based on latest available date
const getApplicationStatus = (item) => {
    // Check in reverse order of progression
    if (item.eCoprDate && item.eCoprDate !== '1970-01-01T00:00:00.000Z') return 'eCOPR';
    if (item.portal2Date) return 'Portal 2';
    if (item.portal1Date) return 'Portal 1';
    // Using finalDecisionDate as a proxy for "BG Check Done/Decision Made pre-Portal"
    // Adjust if backgroundCheckStartDate or another field is more appropriate
    if (item.finalDecisionDate) return 'BG Done/Decision';
    // If only ITA date is present (or earlier stages not tracked here)
    if (item.itaDate) return 'Pending';
    return 'Unknown'; // Should not happen if itaDate is always present
};

const CaseStatusTimelineChart = ({ data, isLoading, error, selectedStreams = [] }) => {
  const theme = useTheme(); // Get theme for breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check if screen is small
// Default selectedStreams to an empty array if not provided
  const echartsRef = useRef(null); // Ref for ReactECharts component

  const chartOptions = useMemo(() => {
    // Initial checks for loading, error, or no base data
    if (isLoading || error || !data || data.length === 0) {
      return {};
    }

    const statusCategories = ['Pending', 'BG Done/Decision', 'Portal 1', 'Portal 2', 'eCOPR'];
    const colors = ['#f9c74f', '#43aa8b', '#f9844a', '#577590', '#90be6d']; // Example colors (Pending, BG, P1, P2, eCOPR)

    // Apply stream filtering before aggregation
    const filteredData = selectedStreams.length > 0
        ? data.filter(item => item.applicationStream && selectedStreams.includes(item.applicationStream))
        : data;

    // Check if data remains after filtering
    if (!filteredData || filteredData.length === 0) {
        // Return minimal config indicating no data for selection
        return {
            title: {
                text: 'No data matches the selected stream(s)',
                left: 'center',
                top: 'center',
                textStyle: { fontSize: 14, color: '#999' }
            }
        };
        // Or just return {} if preferred
        // return {};
    }

    // Aggregate data by day based on aorDate
    const dailyStatusCounts = filteredData.reduce((acc, item) => { // Use filteredData here
      const aorDay = formatDate(item.aorDate);
      if (!aorDay) return acc; // Skip if no valid AOR date

      try {
        const status = getApplicationStatus(item);

        if (status === 'Unknown' || !statusCategories.includes(status)) return acc; // Skip unknown statuses

        if (!acc[aorDay]) {
          acc[aorDay] = { date: aorDay, Pending: 0, 'BG Done/Decision': 0, 'Portal 1': 0, 'Portal 2': 0, eCOPR: 0 };
        }
        acc[aorDay][status]++;
      } catch (e) {
        console.error("Error processing timeline item for daily status chart:", item, e);
      }
      return acc;
    }, {});

    // Sort days chronologically
    const sortedDays = Object.values(dailyStatusCounts).sort((a, b) =>
      new Date(a.date) - new Date(b.date)
    );

    // Prepare data for ECharts series
    const xAxisData = sortedDays.map(day => day.date); // Labels for X-axis (AOR dates)

    const seriesData = statusCategories.map((status, index) => ({
      name: status,
      type: 'bar',
      barMaxWidth: 30, // Added max width for thinner bars
      stack: 'total', // Key for stacking
      emphasis: {
        focus: 'series'
      },
      itemStyle: {
         color: colors[index % colors.length] // Assign color
      },
      data: sortedDays.map(day => day[status] || 0) // Get count for this status for each day
    }));

    return {
      // Add watermark using graphic element
      graphic: echarts.util.merge({
          // Default graphic elements if any
      }, getEchartsWatermark(), true), // Merge watermark config
      title: {
        text: 'Application Status Breakdown by AOR Day', // Updated Title
        left: 'center',
         textStyle: {
           fontSize: 16,
           fontWeight: 'normal'
         }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow' // Use shadow to cover the whole bar
        }
        // Tooltip formatter will automatically show stacked values
      },
      legend: {
        data: statusCategories,
        bottom: 10 // Position legend at the bottom
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%', // Adjust bottom to make space for legend and dataZoom
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: {
             rotate: 30, // Rotate labels if they overlap
             fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        name: 'Count'
      },
      dataZoom: [ // Optional zooming/scrolling
           {
             type: 'inside',
             start: 0,
             end: 100
           },
           {
             type: 'slider',
             start: 0,
             end: 100,
             bottom: 40 // Position slider below legend
           }
         ],
      series: seriesData
    };
  }, [data, isLoading, error, selectedStreams]); // Add selectedStreams dependency

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
         <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
       </Box>
    );
  }

  if (!data || data.length === 0) {
     return (
       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Typography>No data available for status breakdown chart.</Typography>
       </Box>
     );
  }

  // Check if chartOptions indicates "no data" message
  const noDataMessage = chartOptions?.title?.text?.startsWith('No data');

  return (
    <> {/* Wrap in Fragment */}
      {/* Adjust height based on mobile */}
      <Box sx={{ position: 'relative', height: isMobile ? '350px' : '400px', width: '100%' }}>
        <ReactECharts
          ref={echartsRef} // Assign ref to the component
        option={chartOptions}
        style={{ height: '100%', width: '100%' }} // Use 100% to fill the Box
        notMerge={true}
        lazyUpdate={true}
        // Removed onChartReady
      />
      {/* Only show download button if there is actual chart data */}
      {!isLoading && !error && data && data.length > 0 && !noDataMessage && (
        <IconButton
          aria-label="download status breakdown chart"
          onClick={() => {
              const instance = echartsRef.current?.getEchartsInstance();
              handleEchartsDownload(instance, 'Status Breakdown by AOR', selectedStreams.join('-') || 'all');
          }}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }} // Position top-right
          size="small"
        >
          <DownloadIcon />
        </IconButton>
      )}
    </Box>
    </> 
  );
};

export default CaseStatusTimelineChart;